import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LocationGroup } from '../../types/LocationGroup';
import LocationGroupService from '../../services/locationGroupService';
import { LocationService } from '../../services/locationService';
import CreateGroupModal from './CreateGroupModal';
import ImportGroupsModal from './ImportGroupsModal';
import ImportAssignmentsModal from './ImportAssignmentsModal';
import useAuthStore from '../../store/authStore';
import './DragDropGroupAssignment.css';

// Item types for drag and drop
const ItemTypes = {
  DEALER: 'dealer',
};

// Interface for dealer item
interface DealerItem {
  id: string;
  dealerCode?: string;
  dealerName?: string; // Display name for the location
  type: string;
  isAssigned?: boolean;
}

// Props for the DealerCard component
interface DealerCardProps {
  dealer: DealerItem;
  isAssigned: boolean;
  groupName?: string;
  onRemoveFromGroups: (dealerCode: string) => void;
}

// Draggable dealer card component
const DealerCard: React.FC<DealerCardProps> = ({ dealer, isAssigned, groupName, onRemoveFromGroups }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.DEALER,
    item: dealer,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag as any}
      className={`dealer-card ${isDragging ? 'dragging' : ''} ${isAssigned ? 'assigned' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="dealer-code">{dealer.dealerName || dealer.dealerCode || 'Unknown'}</div>
      {isAssigned && groupName && (
        <div className="dealer-actions">
          <div className="dealer-group-tag">{groupName}</div>
          <button 
            className="remove-dealer-btn" 
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFromGroups(dealer.dealerCode || '');
            }}
            title="Remove location from all groups"
          >
            &times;
          </button>
        </div>
      )}
      {isAssigned && !groupName && (
        <div className="dealer-actions">
          <div className="dealer-group-tag">Assigned</div>
        </div>
      )}
    </div>
  );
};

// Props for the GroupDropZone component
interface GroupDropZoneProps {
  group: LocationGroup;
  onDrop: (locationId: number, groupId: number, groupName: string) => void;
  locationMap: Map<number, { name: string; code: string }>;
  onDeleteGroup: (groupId: number) => void;
}

// Droppable group zone component
const GroupDropZone: React.FC<GroupDropZoneProps> = ({ group, onDrop, locationMap, onDeleteGroup }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.DEALER,
    drop: (item: DealerItem) => {
      const locationId = parseInt(item.id.replace('location-', ''));
      onDrop(locationId, group.id!, group.group_name);
      return { groupName: group.group_name };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const locationCount = group.location_ids.length;

  return (
    <div
      ref={drop as any}
      className={`group-drop-zone ${isOver ? 'over' : ''}`}
    >
      <div className="group-header">
        <div className="group-name">{group.group_name}</div>
        <button 
          className="delete-group-btn" 
          onClick={(e) => {
            e.stopPropagation();
            onDeleteGroup(group.id!);
          }}
          title="Delete this group"
        >
          &times;
        </button>
      </div>
      <div className="group-dealer-count">
        {locationCount} location{locationCount !== 1 ? 's' : ''}
      </div>
      {group.group_description && (
        <div className="group-description">
          {group.group_description}
        </div>
      )}
      <div className="assigned-dealers">
        {group.location_ids.map((locationId) => {
          const location = locationMap.get(locationId);
          return (
            <div key={`${locationId}-${group.id}`} className="assigned-dealer-chip">
              {location?.name || location?.code || `Location ${locationId}`}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main component for drag-and-drop group assignment
const DragDropGroupAssignment: React.FC = () => {
  const { user } = useAuthStore();
  const [dealers, setDealers] = useState<DealerItem[]>([]);
  const [groups, setGroups] = useState<LocationGroup[]>([]);
  const [locationMap, setLocationMap] = useState<Map<number, { name: string; code: string }>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [isImportAssignmentsModalOpen, setIsImportAssignmentsModalOpen] = useState<boolean>(false);
  
  // Get organization ID from logged-in user
  const organizationId = user?.organization_id || 0;

  // Load all locations (dealers) available to the user
  const loadAllDealers = async (currentGroups: LocationGroup[] = groups) => {
    try {
      console.log('DEBUG: ========== LOADING LOCATIONS ==========');
      console.log('DEBUG: About to call LocationService.getAllLocations()...');
      
      const locations = await LocationService.getAllLocations();
      console.log('DEBUG: ✅ Locations API call successful!');
      console.log('DEBUG: Total locations count:', locations.length);
      
      // Create location map for quick lookup
      const newLocationMap = new Map();
      locations.forEach(location => {
        newLocationMap.set(location.id, {
          name: location.name,
          code: location.code || location.name
        });
      });
      setLocationMap(newLocationMap);
      
      // Create dealer items from locations
      const dealerItems = locations.map(location => ({
        id: `location-${location.id}`,
        dealerCode: location.code || location.name,
        dealerName: location.name,
        type: ItemTypes.DEALER,
      }));

      console.log('DEBUG: Created dealer items:', dealerItems.length);

      // Mark dealers as assigned if they are in any group
      const assignedLocationIds = new Set<number>();
      currentGroups.forEach(group => {
        group.location_ids.forEach(id => assignedLocationIds.add(id));
      });
      
      const dealersWithAssignment = dealerItems.map(dealer => {
        const locationId = parseInt(dealer.id.replace('location-', ''));
        return {
          ...dealer,
          isAssigned: assignedLocationIds.has(locationId)
        };
      });

      console.log('DEBUG: Setting dealers with assignment status:', dealersWithAssignment.length);
      setDealers(dealersWithAssignment);
      console.log('DEBUG: ========== LOCATIONS LOADED SUCCESSFULLY ==========');
    } catch (err: any) {
      console.error('❌ Error loading locations:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError('Error loading locations. Please try again later.');
    }
  };

  // Load existing location groups
  const loadData = async () => {
    setLoading(true);
    let loadedGroups: LocationGroup[] = [];
    
    try {
      console.log('DEBUG: ========== STARTING LOAD DATA ==========');
      
      // Load all locations FIRST
      console.log('DEBUG: Loading locations first...');
      const locations = await LocationService.getAllLocations();
      console.log('DEBUG: ✅ Locations loaded:', locations.length);
      
      // Create location map for quick lookup
      const newLocationMap = new Map();
      locations.forEach(location => {
        newLocationMap.set(location.id, {
          name: location.name,
          code: location.code || location.name
        });
      });
      setLocationMap(newLocationMap);
      
      // Create dealer items from locations
      const dealerItems = locations.map(location => ({
        id: `location-${location.id}`,
        dealerCode: location.code || location.name,
        dealerName: location.name,
        type: ItemTypes.DEALER,
      }));
      console.log('DEBUG: Created dealer items:', dealerItems.length);
      
      // Try to load groups for the organization (don't fail if this doesn't work)
      try {
        console.log('DEBUG: Loading groups for organization:', organizationId);
        loadedGroups = await LocationGroupService.getGroupsByOrganization(organizationId);
        console.log('DEBUG: ✅ Groups loaded:', loadedGroups.length);
        setGroups(loadedGroups);
      } catch (groupErr: any) {
        console.error('⚠️ Error loading groups (will continue with empty groups):', groupErr);
        setGroups([]);
        // Don't throw - continue with empty groups
      }
      
      // Mark dealers as assigned based on loaded groups
      const assignedLocationIds = new Set<number>();
      loadedGroups.forEach(group => {
        group.location_ids.forEach(id => assignedLocationIds.add(id));
      });
      
      const dealersWithAssignment = dealerItems.map(dealer => {
        const locationId = parseInt(dealer.id.replace('location-', ''));
        return {
          ...dealer,
          isAssigned: assignedLocationIds.has(locationId)
        };
      });
      
      setDealers(dealersWithAssignment);
      console.log('DEBUG: ✅ Dealers with assignment status set');
      
      setError(null);
      console.log('DEBUG: ========== LOAD DATA COMPLETED SUCCESSFULLY ==========');
    } catch (err: any) {
      console.error('❌ Error loading data:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      setError('Error loading locations. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect to load groups and dealers when component mounts
  useEffect(() => {
    if (!organizationId) {
      setError('No organization found for user');
      setLoading(false);
      return;
    }
    
    console.log('DEBUG: Component mounted, loading groups and dealers...');
    console.log('DEBUG: User organization ID:', organizationId);
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Handler when a location is dropped onto a group
  const handleDealerDrop = async (
    locationId: number,
    groupId: number,
    groupName: string
  ) => {
    try {
      // Find the group
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        console.error('Group not found:', groupId);
        return;
      }
      
      // Check if location is already in this group
      if (group.location_ids.includes(locationId)) {
        console.log(`Location ${locationId} is already in group ${groupName}`);
        return;
      }
      
      console.log(`Adding location ${locationId} to group ${groupName}`);
      console.log(`Current location_ids in group:`, group.location_ids);
      
      // Add location to group via API
      const updatedGroup = await LocationGroupService.addLocationToGroup(groupId, locationId);
      console.log(`✅ Location added successfully`);
      console.log(`Updated group from API:`, updatedGroup);
      
      // Update local state using the response from API (this ensures we have the correct data)
      setGroups(prevGroups => 
        prevGroups.map(g => 
          g.id === groupId ? updatedGroup : g
        )
      );
      
      // Update dealer assignment status with the new groups array
      const updatedGroups = groups.map(g => 
        g.id === groupId ? updatedGroup : g
      );
      await loadAllDealers(updatedGroups);
      
      const locationInfo = locationMap.get(locationId);
      setSuccess(`Location "${locationInfo?.name || locationId}" added to group "${groupName}"`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(`Error adding location: ${err.message}`);
      console.error('Error adding location to group:', err);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handler to remove a location from all groups
  const handleRemoveDealerFromAllGroups = async (dealerCode: string) => {
    // Find the location ID from dealer code
    const dealer = dealers.find(d => d.dealerCode === dealerCode);
    if (!dealer) return;
    
    const locationId = parseInt(dealer.id.replace('location-', ''));
    const locationInfo = locationMap.get(locationId);
    
    if (window.confirm(`Are you sure you want to remove location "${locationInfo?.name || dealerCode}" from all groups?`)) {
      try {
        await LocationGroupService.removeLocationFromAllGroups(organizationId, locationId);
        
        // Update local state - remove location from all groups
        const updatedGroups = groups.map(group => ({
          ...group,
          location_ids: group.location_ids.filter(id => id !== locationId)
        }));
        setGroups(updatedGroups);
        
        // Update dealer assignment status
        await loadAllDealers(updatedGroups);
        
        setSuccess(`Location "${locationInfo?.name || dealerCode}" removed from all groups`);
        setTimeout(() => setSuccess(null), 5000);
      } catch (err: any) {
        setError(`Error removing location: ${err.message}`);
        console.error('Error removing location from groups:', err);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  // Handler to delete an entire group by ID
  const handleDeleteGroup = async (groupId: number) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    if (window.confirm(`Are you sure you want to delete the group "${group.group_name}"?`)) {
      try {
        await LocationGroupService.deleteGroup(groupId);
        
        // Update local state
        const updatedGroups = groups.filter(g => g.id !== groupId);
        setGroups(updatedGroups);
        
        // Update dealer assignment status
        await loadAllDealers(updatedGroups);
        
        setSuccess(`Group "${group.group_name}" deleted successfully`);
        setTimeout(() => setSuccess(null), 5000);
      } catch (err: any) {
        setError(`Error deleting group: ${err.message}`);
        console.error('Error deleting group:', err);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  // Handle opening the create group modal
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  // Handle closing the create group modal
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  // Handle creating a new group from modal
  const handleGroupCreated = async (groupName: string, groupDescription: string) => {
    try {
      setError(null);
      
      console.log('Creating group:', { groupName, groupDescription, organizationId });
      const createdGroup = await LocationGroupService.createEmptyGroup(
        organizationId,
        groupName,
        groupDescription || undefined
      );
      console.log('Group created successfully:', createdGroup);
      
      // Add to local state
      setGroups([...groups, createdGroup]);
      setSuccess(`Group "${groupName}" created successfully`);
      setIsCreateModalOpen(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error creating group:', error);
      setError(`Failed to create group: ${error.message}`);
      setTimeout(() => setError(null), 5000);
      throw error; // Re-throw so modal can handle it
    }
  };

  // Handle export groups to CSV
  const handleExportCSV = async () => {
    try {
      setError(null);
      const blob = await LocationGroupService.exportGroupsCSV(organizationId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `location_groups_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Groups exported successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error exporting groups:', error);
      setError(`Failed to export groups: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle import groups from CSV
  const handleImportCSV = async (csvData: string, mode: 'merge' | 'replace') => {
    try {
      const result = await LocationGroupService.importGroupsCSV(organizationId, csvData, mode);
      
      // Reload groups after import
      await loadData();
      
      return result;
    } catch (error: any) {
      console.error('Error importing groups:', error);
      throw error;
    }
  };

  // Handle download template
  const handleDownloadTemplate = () => {
    LocationGroupService.downloadTemplate();
  };

  // Handle opening import modal
  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  // Handle closing import modal
  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
  };

  // Handle export assignments CSV
  const handleExportAssignmentsCSV = async () => {
    try {
      setError(null);
      const blob = await LocationGroupService.exportAssignmentsCSV(organizationId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `location_assignments_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Assignments exported successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error exporting assignments:', error);
      setError(`Failed to export assignments: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle import assignments CSV
  const handleImportAssignmentsCSV = async (csvData: string, mode: 'merge' | 'replace') => {
    try {
      const result = await LocationGroupService.importAssignmentsCSV(organizationId, csvData, mode);
      
      // Reload data after import
      await loadData();
      
      return result;
    } catch (error: any) {
      console.error('Error importing assignments:', error);
      throw error;
    }
  };

  // Handle download assignment template
  const handleDownloadAssignmentTemplate = () => {
    LocationGroupService.downloadAssignmentTemplate();
  };

  // Handle opening import assignments modal
  const handleOpenImportAssignmentsModal = () => {
    setIsImportAssignmentsModalOpen(true);
  };

  // Handle closing import assignments modal
  const handleCloseImportAssignmentsModal = () => {
    setIsImportAssignmentsModalOpen(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="drag-drop-group-assignment-container">
      <h2>Location Group Assignment</h2>
      <p>Drag and drop locations into groups. Locations can be assigned to multiple groups. All financial records for assigned locations will be updated automatically.</p>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <DndProvider backend={HTML5Backend}>
        <div className="drag-drop-container">
          <div className="assignment-area">
            <div className="dealers-area">
              <h3>Available Locations</h3>
              <div className="dealers-list">
                {dealers.map((dealer) => {
                  // Find which groups contain this location
                  const locationId = parseInt(dealer.id.replace('location-', ''));
                  const assignedGroups = groups.filter(g => 
                    g.location_ids.includes(locationId)
                  );
                  const isAssigned = assignedGroups.length > 0;
                  const groupNames = assignedGroups.map(g => g.group_name).join(', ');
                  
                  return (
                    <DealerCard
                      key={dealer.id}
                      dealer={dealer}
                      isAssigned={isAssigned}
                      groupName={groupNames || undefined}
                      onRemoveFromGroups={handleRemoveDealerFromAllGroups}
                    />
                  );
                })}
                {dealers.length === 0 && (
                  <p className="empty-message">
                    No locations available.
                  </p>
                )}
              </div>
            </div>
            
            <div className="dealer-groups">
              <div className="groups-header">
                <h3>Location Groups</h3>
                <div className="groups-actions">
                  <div className="groups-actions-section">
                    <span className="actions-label">Groups:</span>
                    <button 
                      className="export-csv-btn"
                      onClick={handleExportCSV}
                      title="Export groups to CSV"
                    >
                      📤 Export
                    </button>
                    <button 
                      className="import-csv-btn"
                      onClick={handleOpenImportModal}
                      title="Import groups from CSV"
                    >
                      📥 Import
                    </button>
                  </div>
                  <div className="groups-actions-section">
                    <span className="actions-label">Assignments:</span>
                    <button 
                      className="export-csv-btn"
                      onClick={handleExportAssignmentsCSV}
                      title="Export location assignments to CSV"
                    >
                      📤 Export
                    </button>
                    <button 
                      className="import-csv-btn"
                      onClick={handleOpenImportAssignmentsModal}
                      title="Import location assignments from CSV"
                    >
                      📥 Import
                    </button>
                  </div>
                  <button 
                    className="create-group-btn"
                    onClick={handleOpenCreateModal}
                    title="Create new location group"
                  >
                    <span className="plus-icon">+</span> Create Group
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {success && (
                <div className="success-message">
                  {success}
                </div>
              )}

              <div className="groups-list">
                {groups.length === 0 ? (
                  <div className="empty-message">
                    No groups created yet. Click "Create Group" to get started.
                  </div>
                ) : (
                  groups.map((group) => (
                    <GroupDropZone
                      key={group.id}
                      group={group}
                      onDrop={handleDealerDrop}
                      locationMap={locationMap}
                      onDeleteGroup={handleDeleteGroup}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </DndProvider>

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onGroupCreated={handleGroupCreated}
        organizationId={organizationId}
        existingGroupNames={groups.map(g => g.group_name)}
      />

      {/* Import Groups Modal */}
      <ImportGroupsModal
        isOpen={isImportModalOpen}
        onClose={handleCloseImportModal}
        onImport={handleImportCSV}
        onDownloadTemplate={handleDownloadTemplate}
      />

      {/* Import Assignments Modal */}
      <ImportAssignmentsModal
        isOpen={isImportAssignmentsModalOpen}
        onClose={handleCloseImportAssignmentsModal}
        onImport={handleImportAssignmentsCSV}
        onDownloadTemplate={handleDownloadAssignmentTemplate}
      />
    </div>
  );
};

export default DragDropGroupAssignment;